import React, { useMemo } from "react";

import { useChartData, UseChartDataParams } from "@/api/useChartData";
import { HeatMapChartData } from "@/lib/types";

import { Points } from ".";
import HeartMapChartCard from "./Card";
import {
  calculateZoneColors,
  calculateZoneColorsFromRates,
  mapZoneDataToUIProps,
} from "./utils";

/**
 * Props for the HeatMapDataCard component.
 *
 * This interface extends the base chart data parameters but excludes chartType
 * since HeatMapDataCard is specifically for heatmap visualization.
 */
export interface HeatMapDataCardProps extends UseChartDataParams {
  /** The title to display on the heat map card */
  title: string;
  /** Filter by specific result type (optional) */
  resultType?: "out" | "hit" | "whiff" | "contact";
  /** Filter by specific pitch type (optional) */
  pitchType?: string;
}

/**
 * A default color for the points and text in the heatmap chart.
 * The color #6B7280 is used as it has contrast with all the potential colors we have for background.
 */
export const DEFAULT_HEATMAP_COLOR = "#6B7280";

/**
 * Color mapping for different pitch types
 */
export const PITCH_TYPE_COLORS: Record<string, string> = {
  Fastball: "#000000",
  Offspeed: "#22C55E",
  Curveball: "#3B82F6",
  Slider: "#EF4444",
  Cutter: "#F97316",
  Other: "#6B7280",
};

/**
 * HeatMapDataCard Component
 *
 * A data-driven heat map visualization component that fetches real player data
 * and displays it as a colored zone-based heat map for baseball analytics.
 *
 * Features:
 * - Fetches live player chart data from the API
 * - Transforms backend coordinates to visual zones
 * - Calculates zone colors based on point distribution
 * - Shows loading states and error handling
 * - Supports various filters (result type, pitch type, etc.)
 *
 * The component uses a coordinate transformation from backend coordinates
 * (-10 to 10) to SVG coordinates and applies zone-based coloring based on
 * the density of data points in each area.
 *
 * @param props - HeatMapDataCardProps configuration
 * @returns A rendered heat map card with live data visualization
 *
 * @example
 * ```tsx
 * <HeatMapDataCard
 *   title="Fastballs - Strikes"
 *   playerExternalId="player-123"
 *   startDate="2024-01-01"
 *   endDate="2024-12-31"
 *   gameType="game"
 *   playerType="pitcher"
 *   vsHandedness="all"
 *   filters={{ is_fastball: "eq.true", is_strike: "eq.true" }}
 * />
 * ```
 */
export const HeatMapDataCard: React.FC<HeatMapDataCardProps> = ({
  title,
  playerExternalId,
  startDate,
  endDate,
  chartType,
  gameType,
  playerType,
  vsHandedness,
  resultType,
  pitchType,
  filters,
  groupBy,
}) => {
  const { data, isLoading, error } = useChartData({
    playerExternalId,
    startDate,
    endDate,
    gameType,
    playerType,
    vsHandedness,
    chartType,
    resultType,
    pitchType,
    filters,
    groupBy,
  });

  const points: Points = useMemo(() => {
    // Only calculate points for heatmap chart type
    if (chartType !== "heatmap") {
      return { [DEFAULT_HEATMAP_COLOR]: [] };
    }

    if (!Array.isArray(data) || data.length === 0) {
      return { [DEFAULT_HEATMAP_COLOR]: [] };
    }

    // Handle pitch_type grouping
    if (groupBy && groupBy.includes("pitch_type")) {
      const pointsByPitchType: Points = {};

      data.forEach((item) => {
        const chartData = item as HeatMapChartData & { pitch_type?: string };
        const heatmapLocation = chartData.points_heatmap_location;

        if (!heatmapLocation || !Array.isArray(heatmapLocation)) {
          return;
        }

        // Determine the color based on pitch type
        const pitchType = chartData.pitch_type || "Other";
        const color = PITCH_TYPE_COLORS[pitchType] || PITCH_TYPE_COLORS.Other;

        // Initialize array if not exists
        if (!pointsByPitchType[color]) {
          pointsByPitchType[color] = [];
        }

        // Add points with the appropriate color
        const points = heatmapLocation.map((coords) => ({
          x: coords[0],
          y: coords[1],
        }));

        pointsByPitchType[color].push(...points);
      });

      return pointsByPitchType;
    }

    // Default behavior for non-pitch_type grouping
    const chartData = data[0] as HeatMapChartData;
    const heatmapLocation = chartData.points_heatmap_location;

    if (!heatmapLocation || !Array.isArray(heatmapLocation)) {
      return { [DEFAULT_HEATMAP_COLOR]: [] };
    }

    return {
      [DEFAULT_HEATMAP_COLOR]: heatmapLocation.map((coords) => ({
        x: coords[0],
        y: coords[1],
      })),
    };
  }, [data, chartType, groupBy]);

  // Generate labels for pitch types when grouping by pitch_type
  const labels = useMemo(() => {
    if (
      chartType !== "heatmap" ||
      !groupBy ||
      !groupBy.includes("pitch_type")
    ) {
      return undefined;
    }

    // Create a reverse mapping from color to pitch type
    const colorToPitchType: Record<string, string> = {};
    Object.entries(PITCH_TYPE_COLORS).forEach(([pitchType, color]) => {
      colorToPitchType[color] = pitchType;
    });

    // Build labels object based on the points we have
    const labelsMap: Record<string, string> = {};
    Object.keys(points).forEach((color) => {
      if (points[color].length > 0) {
        labelsMap[color] = colorToPitchType[color] || "Other";
      }
    });

    return Object.keys(labelsMap).length > 0 ? labelsMap : undefined;
  }, [chartType, groupBy, points]);

  // Calculate background colors and zone props based on chart type
  const { backgroundColors, zoneProps } = useMemo(() => {
    // For heatmap charts, use point distribution even when data is empty
    if (chartType === "heatmap") {
      return {
        backgroundColors: calculateZoneColors(points),
        zoneProps: {},
      };
    }

    if (!Array.isArray(data) || data.length === 0) {
      return { backgroundColors: {}, zoneProps: {} };
    }

    const chartData = data[0] as Record<string, unknown>;

    // Map zone-based charts to their corresponding field names in chartData
    const zoneBasedCharts: Record<string, string> = {
      max_exit_velocity_by_zone: "max_exit_velocity_per_zone_with_edges",
      avg_exit_velocity_by_zone: "avg_exit_velocity_per_zone_with_edges",
      contact_rate_by_zone: "rate_contact_per_zone_with_edges",
      whiff_rate_by_zone: "rate_whiff_per_zone_with_edges",
      hit_rate_by_zone: "rate_hit_per_zone_with_edges",
    };

    if (Object.keys(zoneBasedCharts).includes(chartType)) {
      // Get the zone data from the API response
      const fieldName = zoneBasedCharts[chartType];
      const zoneData = (chartData[fieldName] as Record<string, number>) || {};

      return {
        backgroundColors: calculateZoneColorsFromRates(zoneData),
        zoneProps: mapZoneDataToUIProps(zoneData, chartType),
      };
    }

    return { backgroundColors: {}, zoneProps: {} };
  }, [data, chartType, points]);

  if (error) {
    return (
      <div className="w-full min-h-[330px] h-full flex items-center justify-center bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-500">Unable to load chart data</p>
      </div>
    );
  }

  return (
    <HeartMapChartCard
      title={title}
      loading={isLoading}
      points={points}
      labels={labels}
      backgroundColors={backgroundColors}
      playerType={playerType}
      {...zoneProps}
    />
  );
};
